import {
  AutocompleteArrayInput,
  AutocompleteInput,
  BooleanInput,
  FormDataConsumer,
  ReferenceArrayInput,
  ReferenceInput,
  SelectInput,
  TextInput,
  required,
} from 'react-admin';

import { NUMBER_OF_ROWS_FOR_TEXTAREA } from '@app/constants';
import { useFormLanguage } from '@app/hooks/useFormLanguage';
import { filterToQueryTickerCode, genericfilterToQuery, genericfilterToQueryMedia } from '@app/utils/filterUtils';
import { DEFAULT_LANGUAGE } from '@app/utils/languages';
import { Grid, Typography } from '@mui/material';

import { CustomRichText } from '@app/components/CustomRichText';
import { LabelNotClickableBoolInput } from '@app/components/LabelNotClickableBoolInput';
import { ImageSelector } from '@app/components/common/ImageSelector';
import { LanguageSelector } from '@app/components/common/LanguageSelector';
import { MultiLingualField } from '@app/components/common/MultiLingualField';
import { GenericObject } from '@app/components/common/Table/DefaultTable/types';

interface CompanyFormProps {
  title: string;
  companyLogo: File | null | undefined;
  handleChangeCompanyLogo(logo: File): void;
}

/**
 * Using this to get the full list of Chix Symbols
 * that the CMS user can browse to get the symbol they want.
 * The chix symbol list gets filtered as the user types more characters
 */
const MAX_NUMBER_OF_CHIX_SYMBOLS = 9999;

export const CompanyForm = ({ companyLogo, title, handleChangeCompanyLogo }: CompanyFormProps) => {
  const { language, handleChangeLanguage } = useFormLanguage();

  return (
    <div>
      <FormDataConsumer>
        {({ formData }) => (
          <div style={{ width: '100%' }}>
            <Typography data-cy={`edit-${title}`} variant="h3" sx={{ marginBottom: 2 }}>
              {title}
            </Typography>
            <LanguageSelector language={language} handleChangeLanguage={handleChangeLanguage} />

            <Grid spacing={2} xl={8} lg={8} md={12} xs={12} container item>
              <Grid item xs={12}>
                <BooleanInput
                  helperText={false}
                  label="Sponsored"
                  source="is_new_ui"
                  parse={(value: boolean) => {
                    if (value === true) {
                      return true;
                    } else {
                      return false;
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <ImageSelector
                  getImageId={(record: GenericObject) => record?.logo_file_base_id as string}
                  getImagePath={(record: GenericObject) => (record?.logo_file_base as GenericObject)?.path as string}
                  image={companyLogo}
                  handleChangeImage={handleChangeCompanyLogo}
                />
              </Grid>
              <Grid item xs={12}>
                <MultiLingualField
                  Component={TextInput}
                  language={language}
                  source="name"
                  label="Name"
                  variant="outlined"
                  fullWidth
                  validate={[required()]}
                  helperText="Maximum 60 characters"
                  inputProps={{ maxLength: 60 }}
                />
              </Grid>
              <Grid item xs={12}>
                <MultiLingualField
                  Component={TextInput}
                  language={language}
                  source="preferred_name"
                  label="Full Company Name"
                  variant="outlined"
                  fullWidth
                  validate={[required()]}
                  helperText="Maximum 40 characters"
                  inputProps={{ maxLength: 40 }}
                />
              </Grid>
              <Grid item xs={12}>
                <MultiLingualField
                  Component={TextInput}
                  language={language}
                  variant="outlined"
                  helperText="Maximum 20 characters"
                  label="Short Name"
                  source="short_name"
                  validate={[required()]}
                  inputProps={{ maxLength: 20 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <MultiLingualField
                  Component={CustomRichText}
                  language={language}
                  variant="outlined"
                  label="Description"
                  multiline
                  helperText={false}
                  rows={NUMBER_OF_ROWS_FOR_TEXTAREA}
                  source="description"
                  fullWidth
                />
              </Grid>
              {formData.is_new_ui && (
                <>
                  <Grid item xs={12}>
                    <MultiLingualField
                      Component={CustomRichText}
                      language={language}
                      variant="outlined"
                      label="Disclosure"
                      multiline
                      helperText={false}
                      rows={NUMBER_OF_ROWS_FOR_TEXTAREA}
                      source="disclosure"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ReferenceArrayInput
                      source="related_vod_ids"
                      reference="media"
                      sort={{ field: 'title', order: 'ASC' }}>
                      <AutocompleteArrayInput
                        variant="outlined"
                        helperText={false}
                        autoHighlight
                        label="Media"
                        filterToQuery={genericfilterToQueryMedia}
                        fullWidth
                        optionText={(record) => record.title[DEFAULT_LANGUAGE]}
                      />
                    </ReferenceArrayInput>
                  </Grid>
                </>
              )}
              <Grid item xs={12}>
                <TextInput variant="outlined" helperText={false} label="Twitter" source="twitter_handle" fullWidth />
              </Grid>
              <Grid item xs={6}>
                <ReferenceInput source="industry_id" reference="industries">
                  <SelectInput
                    variant="outlined"
                    helperText={false}
                    label="I ndustry"
                    fullWidth
                    validate={[required()]}
                    optionText={(record) => record.name[DEFAULT_LANGUAGE]}
                  />
                </ReferenceInput>
              </Grid>
              <Grid item xs={6}>
                <ReferenceInput
                  source="chix_symbol_id"
                  reference="chixsymbols"
                  perPage={MAX_NUMBER_OF_CHIX_SYMBOLS}
                  sort={{ order: 'ASC' }}>
                  <AutocompleteInput
                    label="Ticker Code"
                    variant="outlined"
                    helperText={false}
                    fullWidth
                    optionText={(record) => record.code}
                    filterToQuery={filterToQueryTickerCode}
                  />
                </ReferenceInput>
              </Grid>
              <Grid item xs={12}>
                <ReferenceArrayInput source="related_topic_ids" reference="taxonomies">
                  <AutocompleteArrayInput
                    variant="outlined"
                    helperText={false}
                    autoHighlight
                    label="Related Topics"
                    filterToQuery={genericfilterToQuery}
                    fullWidth
                    optionText={(record) => (record?.category_name ? record?.category_name[DEFAULT_LANGUAGE] : '')}
                  />
                </ReferenceArrayInput>
              </Grid>
              <Grid item xs={12}>
                <LabelNotClickableBoolInput label="Published" source="company_profile" />
              </Grid>
            </Grid>
          </div>
        )}
      </FormDataConsumer>
    </div>
  );
};
